import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import NavbarT from '../../components/navbarTechnican';
import Footer from '../../homeComponents/footer';
import car from '../../images/progress/car.png';

const TechnicianTasksPage = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userDetails, setUserDetails] = useState({});
  const [sortOrder, setSortOrder] = useState('latest');

  const API_URL = process.env.REACT_APP_API_URL;

  const userId = useSelector((state) => state.auth.user.id);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        // Backend scopes this to the authenticated technician's own services
        // server-side (GET /my-tasks) — used to call the admin-only /getAll,
        // which 403'd for a technician role entirely.
        const response = await axios.get(`${API_URL}/api/services/my-tasks`);
        const services = response.data;

        const filteredServices = services.filter(
          (service) => service.status !== 'Completed' && !service.pdiReportId
        );

        setTasks(filteredServices);

        await Promise.all(
          filteredServices.map(async (service) => {
            const customerId = service.userId && typeof service.userId === 'object' ? service.userId._id : null;
            if (customerId) {
              await getUser(customerId);
            }
          })
        );
      } catch (err) {
        setError('Error fetching service details');
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchTasks();
    } else {
      console.log('User ID is not available');
    }
  }, [userId]);

  const getUser = async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users/${userId}/get`);
      setUserDetails((prevState) => ({
        ...prevState,
        [userId]: response.data,
      }));
    } catch (err) {
      console.error(`Error fetching user details for userId ${userId}:`, err);
    }
  };

  const handleTaskClick = (taskId, inspectionType) => {
    switch (inspectionType) {
      case "Pre Delivery Inspection":
        navigate(`/technician/report-pre-delivery/${taskId}`);
        break;
      case "Used Car Inspections":
        navigate(`/technician/report-used-car/${taskId}`);
        break;
      case "Pre Trip Inspections":
        navigate(`/technician/report-pre-trip/${taskId}`);
        break;
      default:
        console.error(`Unhandled inspection type: ${inspectionType}`);
        // Optionally, you can show an error message or handle the default case
        // For example, redirect to a generic error page or log the issue
        break;
    }
  };

  const sortedTasks = [...tasks].sort((a, b) => {
    const dateA = new Date(a.deliveryDate);
    const dateB = new Date(b.deliveryDate);
    return sortOrder === 'latest' ? dateB - dateA : dateA - dateB;
  });

  return (
    <div className='font-clash'>

<Helmet>
              <title>Technician Tasks</title>
              <meta
                name="keywords"
                content="Pdi, Bengaluru, Bangalore, Kardo, Inspection, Ze, Pre delivery, Services, Car, Cars, Vaidya, BHP, Team, CID, Expert, In, Best, 24, BOSS, Dail, Just, New car, Checklist, Detailed, Report, Inspect, Reliable, Blog, Used car inspection, Buying a new car"
              />
              <meta
                name="description"
                content="Explore our reliable pre-delivery inspection services for new and used cars. Detailed reports, expert insights, and the best service in Bangalore and beyond."
              />
            </Helmet>
      <NavbarT />
      <div className="flex flex-col items-center justify-start mx-auto p-6 bg-banner min-h-screen shadow-lg z-20">
        {loading ? (
          <div>Loading tasks...</div>
        ) : error ? (
          <div>{error}</div>
        ) : (
          <>
            <div className="flex justify-center mb-4 text-white w-full mt-28">
              <label className="">Sort by:
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="border rounded-lg p-2 text-black w-full sm:w-auto ml-4"
              >
                <option value="latest">Latest First</option>
                <option value="oldest">Oldest First</option>
              </select>
              </label>
            </div>

            {sortedTasks.length === 0 ? (
              <p className="text-center text-gray-500">No tasks assigned to you yet.</p>
            ) : (
              sortedTasks.map((task, index) => (
                <div
                  key={task._id}
                  className={`flex flex-col lg:flex-row ${
                    index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  } items-center justify-center cursor-pointer w-full max-w-5xl bg-[#1F1F1F] border border-[#ADD144] p-6 rounded-lg shadow-2xl mt-3 mb-14`}
                  onClick={() => handleTaskClick(task._id, task.inspectionType)}
                >
                  {/* Car Image Section */}
                  <div className="lg:w-2/3 w-full flex flex-col justify-center items-center p-4 relative">
                    <img
                      src={task.vehicleImageUrl || car}
                      alt="Car"
                      className="h-auto rounded-lg w-full sm:w-[300px] lg:mb-5 mb-0 lg:w-[500px]"
                    />
                     <div className="flex lg:flex-nowrap flex-wrap w-full items-center justify-center gap-2 sm:gap-6 lg:gap-10 border-t border-l border-r border-[#ADD144] shadow-xl p-4 sm:p-6 rounded-lg mt-4 lg:mt-0">
                      {/* Individual Stats */}
                      <div className="flex items-center justify-center gap-2 sm:gap-6">
                        <div className="flex flex-col items-center lg:items-start">
                          <p className="text-[#F0FFC5] text-lg sm:text-xl lg:text-2xl font-semibold">
                          {task.bhp || 'N/A'}
                          </p>
                          <p className="text-[#FFFF] text-sm sm:text-base lg:text-lg">
                            BHP
                          </p>
                        </div>
                        <div className="w-[2px] h-8 sm:h-10 lg:h-12 bg-[#ADD144] hidden sm:block"></div>
                        <div className="flex flex-col items-center lg:items-start">
                          <p className="text-[#F0FFC5] text-lg sm:text-xl lg:text-2xl font-semibold">
                          {task.airbags || 'N/A'}
                          </p>
                          <p className="text-[#FFFF] text-sm sm:text-base lg:text-lg">
                            Airbags
                          </p>
                        </div>
                      </div>
                      <div className="w-[2px] h-8 sm:h-10 lg:h-12 bg-[#ADD144] hidden sm:block"></div>
                      <div className="flex flex-col items-center lg:items-start">
                        <p className="text-[#F0FFC5] text-lg sm:text-xl lg:text-2xl font-semibold">
                        {task.mileage || 'N/A'}
                        </p>
                        <p className="text-[#FFFF] text-sm sm:text-base lg:text-lg">
                          Mileage (kmpl)
                        </p>
                      </div>
                      <div className="w-[2px] h-8 sm:h-10 lg:h-12 bg-[#ADD144] hidden sm:block"></div>
                      <div className="flex flex-col items-center lg:items-start">
                        <p className="text-[#F0FFC5] text-lg sm:text-xl lg:text-2xl font-semibold">
                        {task.ncap || 'N/A'}
                        </p>
                        <p className="text-[#FFFF] text-sm sm:text-base lg:text-lg">
                          NCAP
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Task Details Section */}
                  <div className="w-full lg:w-1/2 flex flex-col items-start p-4">
                    <ul className="divide-y divide-gray-200">
                      <li className="p-4">
                        <div className="flex flex-col items-start sm:items-center sm:flex-row sm:gap-5">
                          <p className="relative mb-4 text-[#FFFFFF] text-lg sm:text-2xl font-semibold pb-2 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-full after:h-[3px] after:bg-[linear-gradient(to_bottom_right,_rgba(240,255,197,0),_rgba(240,255,197,0.2))]">
                            <span>{task.vehicleMake || 'N/A'} {task.vehicleModel}</span>
                          </p>
                          <div className="bg-[#0B7300] rounded-full p-2 text-[10px] sm:text-[12px]">
                          Reach at {new Date(task.deliveryDate).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                          </div>
                        </div>
                        <p className="font-semibold py-2 text-white">Booking ID: {task._id}</p>
                        <p className="font-semibold py-2 text-white">Inspection Type: {task.inspectionType}</p>
                        <p className="font-semibold py-2 text-white">Customer Name: {task.customerName || 'N/A'}</p>
                        <p className="font-semibold py-2 text-white">Vehicle Model: {task.vehicleModel || 'N/A'}</p>
                        <p className="font-semibold py-2 text-white">Vehicle Make: {task.vehicleMake || 'N/A'}</p>
                        <p className="font-semibold py-2 text-white">Vehicle Varient: {task.variant || 'N/A'}</p>
                        <p className="font-semibold py-2 text-white">Address: {task.address || 'N/A'}</p>
                        <p className="font-semibold py-2 text-white">Allotted VIN: {task.allottedVin || 'N/A'}</p>
                        <p className="font-semibold py-2 text-white">
                          Delivery Date: {new Date(task.deliveryDate).toLocaleDateString()}
                        </p>
                        <p className="font-semibold py-2 text-white">Status: {task.status}</p>
                      </li>
                    </ul>
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default TechnicianTasksPage;
